import { createContext, useContext, type ReactNode } from 'react';
import type { InlineOverrideField } from './inlineOverrideField';
import {
  useInlineOverrideFields,
  type InlineOverrideTarget,
} from './inlineOverrideSources';

interface InlineOverrides {
  fields: ReadonlyMap<string, InlineOverrideField>;
  canEdit: boolean;
}

const InlineOverridesContext = createContext<InlineOverrides | null>(null);

/**
 * Makes one (item, location)'s overridable fields available to the summary
 * cards, so a card value can say it is overridden and open its own editor.
 * Mounted only when the page has resolved to a single location — an override is
 * stored per location, so a scope of several has nothing to write to.
 */
export function InlineOverridesProvider({
  target,
  canEdit,
  children,
}: {
  target: InlineOverrideTarget;
  canEdit: boolean;
  children: ReactNode;
}) {
  const fields = useInlineOverrideFields(target);
  const value = {
    fields: new Map(fields.map((field) => [field.id, field])),
    canEdit,
  };
  return (
    <InlineOverridesContext.Provider value={value}>
      {children}
    </InlineOverridesContext.Provider>
  );
}

/** The overridable field with this id, or null outside a single location. */
// react-doctor-disable-next-line react-doctor/only-export-components -- hook reads this file's private context; a sibling file would have to export the context too
export function useInlineOverrideField(
  id: string | undefined,
): { field: InlineOverrideField; canEdit: boolean } | null {
  const context = useContext(InlineOverridesContext);
  const field = id === undefined ? undefined : context?.fields.get(id);
  return context && field ? { field, canEdit: context.canEdit } : null;
}
